import { useState, type ReactNode } from "react";
import { IonContent, IonPage } from "@ionic/react";

import { BarChart } from "../components/BarChart";
import { Hero } from "../components/Hero";
import { I } from "../components/icons";
import { Insight } from "../components/Insight";
import { ProgressBar } from "../components/ProgressBar";
import { NavRow, RowGroup } from "../components/Rows";
import { Segmented } from "../components/Segmented";
import { Screen } from "../components/Shell";
import { Tag } from "../components/Tag";
import { TopBar } from "../components/TopBar";
import { cycleOn } from "../domain/cycle";
import { DOW_LABELS, addDays, dayMonth, parseDay, todayStr } from "../domain/date";
import type { Insight as InsightData } from "../domain/insight";
import { stepsWindow, walksWindow } from "../domain/movement";
import { challenge } from "../domain/progress";
import { CAL_SENTENCE, CAL_WORD, calendarView, hoursMinutes, periodRuns, workSpan } from "../domain/progressView";
import {
  WHO_MINUTES,
  WHO_STRENGTH_DAYS,
  activeByWeek,
  activeInWindow,
  averageDay,
  consistency,
  trainingInsights,
  weekVsLast,
  whoMinutes,
} from "../domain/training";
import { programmeOf, talkInsight } from "../domain/readiness";
import { hasTreadmill } from "../domain/treadmill";
import { useKaya } from "../store/kayaStore";
import "./Progress.css";
import "./ProgressTraining.css";

type TrainWindow = 7 | 28 | 90;

const WINDOWS: { value: TrainWindow; label: string }[] = [
  { value: 7, label: "7 days" },
  { value: 28, label: "28 days" },
  { value: 90, label: "90 days" },
];

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** "3 h 10" with the h set as a unit between the two figures; "35 min" as a figure and unit. */
function activeFigure(min: number): { figure: ReactNode; unit?: string } {
  if (min < 60) return { figure: min, unit: "min" };
  const h = Math.floor(min / 60);
  const m = min % 60;
  return {
    figure: (
      <>
        {h}
        <span className="k-hero__unit pt-h">h</span>
        {m ? String(m).padStart(2, "0") : null}
      </>
    ),
  };
}

const kcal = (n: number) => `~${n.toLocaleString()}`;

/** "a vs b", a missing side as "–", and "–" alone when neither side has anything to compare. */
function versus<T>(p: { now: T | null; prev: T | null }, f: (v: T) => string): string | null {
  if (p.now === null && p.prev === null) return null;
  return `${p.now === null ? "–" : f(p.now)} vs ${p.prev === null ? "–" : f(p.prev)}`;
}

/**
 * /progress/training — replaces Consistency (ADR-033). Active time as the one answer, WHO's
 * 150 minutes and two strength days beside it, then the weeks, this week against the last, the
 * four-week calendar, progression and rest. Its "last 7 days" include today.
 */
export default function ProgressTraining() {
  const S = useKaya((s) => s.S);
  const [w, setW] = useState<TrainWindow>(7);
  const today = todayStr();
  // Movement's averages end yesterday: today's count is still climbing.
  const yesterday = addDays(today, -1);

  const total = activeInWindow(S, today, w);
  const perDay = averageDay(S, today, w);
  const who = whoMinutes(S, today);
  const vs = weekVsLast(S, today);
  const { now: sNow, prev: sPrev } = vs.sessions;
  const sessionsVs = versus(
    { now: sNow && `${sNow.done}/${sNow.of} so far`, prev: sPrev && `${sPrev.done}/${sPrev.of}` },
    String
  );
  const weeks = activeByWeek(S, today, 12);
  const c = consistency(S, today);
  const cal = calendarView(S, today);
  const periods = cycleOn(S) ? periodRuns(cal.cells, "period") : [];
  const predicted = cycleOn(S) ? periodRuns(cal.cells, "predicted") : [];
  const ch = challenge(S);
  const tm = hasTreadmill(S);

  const ins = trainingInsights(S, today);
  const pick = (key: string): InsightData[] => ins.filter((x) => x.key === key);
  const one = (key: string): InsightData | undefined => ins.find((x) => x.key === key);

  const steps = stepsWindow(S, yesterday, 28);
  const walks = walksWindow(S, yesterday, 28);
  const work = workSpan(S, today, 28);

  // Console when every run in the two weeks read its distance off the machine; otherwise estimated.
  const runs = Array.from({ length: 14 }, (_, i) => S.logs[addDays(today, -i)]?.treadmill).filter(
    (t): t is NonNullable<typeof t> => !!t
  );
  const kmTag = runs.every((t) => t.kmFromConsole) ? "console" : "est";

  const fig = activeFigure(total);
  const last = weeks.length - 1;

  const overreach = one("training.overreach");
  const tests = one("training.tests");
  const talk = talkInsight(S, today);
  // kcal never for a Steady person: for chair and counter moves the estimate is noise (ADR-054)
  const steady = programmeOf(S) === "steady";

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <TopBar title="Training" back={{ to: "/progress", label: "Back to Progress" }} />
          <div className="pg">
            <Hero
              ariaLabel={`Active time, last ${w} days`}
              label={`Active time · last ${w} days`}
              figure={fig.figure}
              unit={fig.unit}
              line={`${total.toLocaleString()} min · ${perDay === null ? "–" : perDay} min a training day`}
              visual={
                <div className="pt-who">
                  <ProgressBar
                    value={who.minutes}
                    max={Math.max(who.minutes, WHO_MINUTES)}
                    target={WHO_MINUTES}
                    label={`WHO ${WHO_MINUTES} min a week`}
                    detail={
                      who.met ? (
                        <span className="pt-met">
                          {I.check}
                          met
                        </span>
                      ) : (
                        `${WHO_MINUTES - who.minutes} min to go`
                      )
                    }
                    ariaLabel={`${who.minutes} of WHO's ${WHO_MINUTES} active minutes in the last 7 days${who.met ? ", met" : ""}`}
                  />
                  <p className="k-hero__line">
                    {who.strengthDays} strength {who.strengthDays === 1 ? "day" : "days"} ·{" "}
                    {who.strengthMet ? <span className="pt-met">met</span> : `WHO asks for ${WHO_STRENGTH_DAYS}`} · this
                    week so far {hoursMinutes(vs.soFar)}
                  </p>
                </div>
              }
            />

            <Segmented options={WINDOWS} value={w} onChange={setW} ariaLabel="Active time window" />

            <RowGroup heading="Active time by week">
              <div className="pg-block">
                <BarChart
                  mode="single"
                  ariaLabel={
                    weeks.length
                      ? `Active minutes by week, last ${weeks.length} full weeks: ${weeks.map((x) => `${dayMonth(x.weekStart)} ${x.minutes}`).join(", ")}`
                      : "No full week yet"
                  }
                  bars={weeks.map((x, i) => ({
                    key: x.weekStart,
                    label: (last - i) % (weeks.length <= 6 ? 1 : 4) === 0 ? dayMonth(x.weekStart) : "",
                    name: `Week of ${dayMonth(x.weekStart)}`,
                    value: x.minutes,
                    current: i === last,
                  }))}
                  rule={{ value: 150, label: "WHO" }}
                  format={hoursMinutes}
                  per="week"
                  readout={(b) => `${b.name} · ${hoursMinutes(b.value ?? 0)} active`}
                  empty="Active time by week appears after your first full week."
                />
              </div>
              {pick("training.active").map((x) => (
                <Insight key={x.key} insight={x} />
              ))}
              <NavRow icon={I.list} title="Every day's active time" to="/progress/records/active" />
            </RowGroup>

            <RowGroup heading="This week vs last">
              <NavRow
                kind="value"
                icon={I.check}
                title="Sessions"
                value={sessionsVs ?? "–"}
              />
              {tm && (vs.treadKm.now !== null || vs.treadKm.prev !== null) ? (
                <NavRow
                  kind="value"
                  icon={I.treadmill}
                  title="Treadmill"
                  value={
                    <>
                      {versus(vs.treadKm, String)} km{runs.length ? <> <Tag tone={kmTag} /></> : null}
                    </>
                  }
                />
              ) : null}
              {steady ? null : (
                <NavRow
                  kind="value"
                  icon={I.flame}
                  title="Work"
                  value={
                    versus(vs.work, kcal) === null ? (
                      "–"
                    ) : (
                      <>
                        {versus(vs.work, kcal)} kcal <Tag tone="est" />
                      </>
                    )
                  }
                />
              )}
              <NavRow kind="value" icon={I.clock} title="Active" value={versus(vs.active, hoursMinutes) ?? "–"} />
            </RowGroup>

            <section className="k-group" aria-label="Consistency">
              <h2 className="k-group__h">Consistency</h2>
              <div className="k-rows">
                <div className="k-chapter__leads k-chapter__leads--split">
                  <div className="k-chapter__lead">
                    <span className={`k-chapter__leadfig${c.of ? "" : " is-empty"}`}>
                      {c.of ? c.pct : "–"}
                      {c.of ? <span className="k-chapter__leadunit">%</span> : null}
                    </span>
                    <span className="k-chapter__leadlabel">Done · 28 days</span>
                    <span className="k-chapter__leadnote">
                      {c.n} of {c.of} scheduled
                    </span>
                  </div>
                  <div className="k-chapter__lead">
                    <span className="k-chapter__leadfig">
                      {c.streak}
                      <span className="k-chapter__leadunit">{c.streak === 1 ? "day" : "days"}</span>
                    </span>
                    <span className="k-chapter__leadlabel">Streak</span>
                    <span className="k-chapter__leadnote">longest {c.longest}</span>
                  </div>
                </div>
                <div className="pg-block">
                  <div className="pg-cal">
                    {WEEKDAYS.map((d) => (
                      <span key={d} className="pg-cal__h" aria-hidden="true">
                        {d}
                      </span>
                    ))}
                    {cal.cells.map((cell) => {
                      const d = parseDay(cell.ds);
                      const periodWords =
                        cell.period === "period"
                          ? ", period day"
                          : cell.period === "predicted"
                            ? ", period expected"
                            : "";
                      return (
                        <div
                          key={cell.ds}
                          role="img"
                          aria-label={`${DOW_LABELS[d.getDay()]} ${d.getDate()}: ${CAL_SENTENCE[cell.state]}${periodWords}`}
                          className={`pg-cal__d pg-cal__d--${cell.state}${cell.period ? ` pg-cal__d--${cell.period}` : ""}`}
                        >
                          <span className="pg-cal__n">{d.getDate()}</span>
                          <span className="pg-cal__w">{CAL_WORD[cell.state]}</span>
                        </div>
                      );
                    })}
                  </div>
                  <p className="pg-cap">
                    {cal.scheduled
                      ? `${cal.covered} of ${cal.scheduled} scheduled days done or covered`
                      : "No scheduled days yet in these four weeks"}
                    {periods.length ? ` · period ${periods.join(", ")}` : ""}
                    {predicted.length ? ` · period expected ${predicted.join(", ")}` : ""}
                  </p>
                </div>
                {[...pick("training.consistency"), ...pick("training.rest")].map((x) => (
                  <Insight key={x.key} insight={x} />
                ))}
              </div>
            </section>

            {tests ? (
              <RowGroup heading="Tests">
                <Insight insight={tests} first />
              </RowGroup>
            ) : null}

            <RowGroup heading="Progression">
              {/* the talk test's answers in words (ADR-065), after the existing ones */}
              {[...pick("training.progression"), ...(tm ? pick("training.treadmill") : []), ...(talk ? [talk] : [])].map((x, i) => (
                <Insight key={x.key} insight={x} first={i === 0} />
              ))}
            </RowGroup>

            <RowGroup heading="Rest and overreach">
              {overreach ? <Insight insight={overreach} first /> : null}
              <NavRow
                kind="value"
                icon={I.snowflake}
                tone="sky"
                title="Freezes"
                subtitle="each covers one missed day"
                value={`${S.freezes} left`}
              />
              <NavRow
                kind="value"
                icon={I.target}
                title="Weekly challenge"
                subtitle={ch.c.n}
                value={ch.done ? "done" : `${Math.round(ch.a * 10) / 10} of ${ch.n}`}
              />
            </RowGroup>

            <RowGroup heading="Connected">
              <NavRow
                icon={I.footsteps}
                title="Movement"
                subtitle={`${steps.avg === null ? "–" : steps.avg.toLocaleString()} steps a day · walks ${
                  walks.km === null ? "–" : walks.km
                } km`}
                to="/progress/movement"
              />
              {steady ? null : (
                <NavRow
                  icon={I.flame}
                  title="Energy"
                  subtitle={work.avg === null ? "Work – kcal a day" : `Work ${kcal(work.avg)} kcal a day est.`}
                  to="/progress/energy"
                />
              )}
            </RowGroup>
          </div>
        </Screen>
      </IonContent>
    </IonPage>
  );
}
